import { FC, useEffect } from 'react'
import { Divider } from 'antd'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerText from '../../../../components/custom_components/Text'
import { useCurrency } from '../../../../context/currency-context'
import { useCurrentContractWithCareerPathEvaluation } from '../../../../graphql/contract/hooks/useCurrentContractWithCareerPathEvaluation'
import { useFindBonusByEmployeeIdAndMeetingId } from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import { CountrySettingsFragment, FullMeetingFragment, GovernmentJobTitle, MeetingStepStatus, MeetingStepViewId } from '../../../../types'
import { AppraisalContractHistory } from '../../contract/ContractHistory'
import { CurrentContractOverview } from '../../contract/CurrentContractComponent'
import { CurrentPendingContractChanges } from '../../contract/CurrentPendingContractChangesComponent'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { toEligibleFlexibleBonusOverride } from '../shared/flexibleBonusUtil'
import { calculateBonusWithSalary } from '../shared/targetBonusCalculation-util'
import moment from 'moment-timezone'
import { useGovernmentJobTitlesByHiringOfficeId } from '../../../../graphql/government-job-title/hooks/useGovernmentJobTitlesByHiringOfficeId'
import { retrieveContractStartAndEndDateFromContract } from '../../../profile/contract/utils/contractUtil'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
  countrySettings: CountrySettingsFragment
}

const ManagerMeetingStepContractChanges: FC<Props> = ({ backBtn, nextBtn, meeting, countrySettings }) => {
  const { newTargetsReferenceYear } = meeting.targetsCalculationHelper
  const [t] = useTranslation()
  const { currencyConversionData } = useCurrency()

  const { data, loading, error } = useCurrentContractWithCareerPathEvaluation(
    meeting.employee.id,
    newTargetsReferenceYear,
    true,
    true,
    // Bad practice but in this case currentContract will always exist since you can't have appraisal with employee who doesn't have contract
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    meeting.employee.currentContract!.legalEntity.country,
  )

  const {
    data: findBonusAmountData,
    error: findBonusAmountDataError,
    loading: findBonusAmountLoading,
  } = useFindBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id)

  const [getGovernmentJobTitles, { data: gjtData, loading: gjtLoading, error: gjtError }] = useGovernmentJobTitlesByHiringOfficeId()

  useEffect(() => {
    if (
      !!data &&
      !!data.currentContract &&
      !!data.currentContract.contractGovernmentJobTitle &&
      countrySettings.hasGovernmentJobTitleReferenceLevel
    ) {
      getGovernmentJobTitles({
        variables: {
          hiringOfficeId: data.currentContract.contractGovernmentJobTitle.governmentJobTitle.hiringOffice.id,
          excludePartnerLevels: true,
        },
      })
    }
  }, [getGovernmentJobTitles, countrySettings, data])

  if (
    loading ||
    findBonusAmountLoading ||
    error ||
    findBonusAmountDataError ||
    !data ||
    !data.currentContract ||
    !data.careerPath ||
    !findBonusAmountData
  ) {
    return (
      <LoadingAndErrors isLoading={loading || findBonusAmountLoading || gjtLoading} error={error || findBonusAmountDataError || gjtError} />
    )
  }

  const flexibleBonusOverride = toEligibleFlexibleBonusOverride(
    findBonusAmountData.findBonusByEmployeeIdInAppraisalSeasonOfMeeting,
    data.currentContract.currencyForPayments,
    currencyConversionData,
    data.branchOfficeByEmployee.timezone,
    newTargetsReferenceYear,
    data.contractsInYear,
    data.targetBonusOfYear,
    data.contractChangesByEmployee,
  )

  const currentAnnualBonus = data.currentContract.targetBonus ? data.currentContract.targetBonus.amount : 0

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(data.currentContract)

  const prorataCalc = calculateBonusWithSalary(
    data.branchOfficeByEmployee.timezone,
    data.isBonusValidationEnabled,
    newTargetsReferenceYear,
    data.contractChangesByEmployee,
    data.targetBonusOfYear,
    data.salaryOfYear,
    data.currentContract.annualSalary ? data.currentContract.annualSalary.amount : 0,
    currentAnnualBonus,
    currencyConversionData,
    data.currentContract.currencyForPayments,
    contractStartDate,
    contractEndDate,
    data.contractsInYear,
    flexibleBonusOverride,
  )

  const targetBonusByChanges =
    data.contractChangesByEmployee &&
    data.contractChangesByEmployee.find((cc) => {
      const changes = JSON.parse(cc.changes)
      const targetBonus = changes.bonus
      return targetBonus !== undefined
    })

  const yearOfChanges =
    targetBonusByChanges && targetBonusByChanges.validFrom ? moment.utc(targetBonusByChanges.validFrom).year() : undefined

  const effectiveBonusAmount = prorataCalc.flexibleBonusAmount ?? prorataCalc.amount

  const newBonus = {
    effectiveBonusAmount: effectiveBonusAmount,
    annualAmount:
      targetBonusByChanges && yearOfChanges && [newTargetsReferenceYear, newTargetsReferenceYear - 1].includes(yearOfChanges)
        ? JSON.parse(targetBonusByChanges.changes).bonus
        : currentAnnualBonus,
    prorata: prorataCalc.amount,
    prorataPartials: prorataCalc.partials,
    currency: data.currentContract.currencyForPayments,
    employeeTimezone: data.branchOfficeByEmployee.timezone,
  }

  return (
    <div data-e2e-manager-meeting-step-contract-changes>
      <MeetingBackground>
        <PerText appearance="heading" mb="m" color="primary">
          {t('appraisalMeeting.stepContractChanges.pageHeaderMeeting')}
        </PerText>
        <PerText color="secondary" mb="m">
          {t('appraisalMeeting.stepContractChanges.pageDescriptionManager')}
        </PerText>
        <CurrentContractOverview
          employeeTimezone={meeting.employee.branchOffice.timezone}
          newBonus={newBonus}
          newTargetsAllowed={meeting.allowedMonetaryTargets.newTargetsAllowed}
          referenceYear={newTargetsReferenceYear}
          currentContract={data.currentContract}
          careerPathEvaluation={data.careerPathEvaluation}
          careerPathData={data.careerPath}
          isPreparation
        />
        <AppraisalContractHistory
          employeeId={meeting.employee.id}
          employeeTimezone={meeting.employee.branchOffice.timezone}
          isMonthlySalaryDisplayed={data.currentContract.isMonthlySalaryDisplayed}
          monthlySalaryMonthsInYear={data.currentContract.monthlySalaryMonthsInYear}
        />
        <Divider />
        <CurrentPendingContractChanges
          meeting={meeting}
          currentContract={data.currentContract}
          careerPathEvaluation={data.careerPathEvaluation ? data.careerPathEvaluation : null}
          careerPathData={data.careerPath}
          isPreparation
          countrySettings={countrySettings}
          governmentJobTitles={
            !!gjtData && !!gjtData.governmentJobTitlesByHiringOfficeId
              ? (gjtData.governmentJobTitlesByHiringOfficeId as GovernmentJobTitle[])
              : undefined
          }
        />
      </MeetingBackground>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        {nextBtn && nextBtn(MeetingStepStatus.Done)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.ContractChanges} stepStatus={MeetingStepStatus.Done} />
    </div>
  )
}

export default ManagerMeetingStepContractChanges
